import { ChangeEvent, FocusEvent, useEffect, useState } from 'react';
import { memo, useRef } from 'react';
import { useKeyboardIntegration } from '../../../../context/Keyboard/keyboard';
import { useFormikContext, useField } from 'formik';
import { BaseTextField } from '../../BaseTextField/BaseTextField';
import { useKioskState } from '../../../../context/Kiosk/kiosk';
import { useLocale } from '../../../../context/Localization/localization';
import { GetValueReturn, OnInputChangeProps, OnInputChangeReturn, SetValueProps } from '../../../../context/Keyboard/keyboard.types';
import { KioskFormEmailFieldProps } from '../FormEmailField.types';
import { EmailPatternMask } from '../EmailPatternMask/EmailPatternMask';
import { TextFieldValidation } from '../../../../helpers/FormFieldValidation';
import { useScrollToOnError } from '../../../../helpers/useScrollTo';

export const KioskFormEmailField = memo(
    ({
        name,
        id,
        title,
        label,
        ariaLabel,
        disabled,
        required,
        autoFocus,
        initiallyMasked,
        keyboardLayout,
        shiftAfterSpace,
        shiftAfterPeriod,
        shiftAfterAmpersand,
        hideKeyboardOnClickOut,
        capsLock,
        fullWidth,
        seventyFivePercentWidth,
        tabFocusRefContext,
        onChangeCallback,
        noValidation,
    }: KioskFormEmailFieldProps) => {
        const [field, meta] = useField<string>({
            name: name,
            validate: noValidation ? undefined : TextFieldValidation({
                required: required ?? false,
                label,
                name,
                validationType: 'email',
            }),
        });
        const { submitCount, setFieldValue } = useFormikContext();

        /**
         * A ref to the HTMLInputElement in the MUI TextField.
         */
        const inputRef = useRef<HTMLInputElement>(null);
        /**
         * The focus ref to prevent a stale state when the on-screen keyboard
         * calls any functions that need to know if the field is focused or not.
         */
        const isFocused = useRef<boolean>(false);
        /**
         * The input value ref to prevent a stale state when the on-screen keyboard
         * calls any functions that use the internal value state.
         */
        const valueRef = useRef<string>(field.value ?? '');
        /**
         * The caret position ref to prevent a stale state when the on-screen keyboard
         * calls any functions that use the internal caret position state.
         */
        const caretPositionRef = useRef<number | null>(null);
        /**
         * Boolean state to update the email pattern mask once the field has been focused
         * so that it never remasks the value.
         */
        const [wasUnmasked, setWasUnmasked] = useState<boolean>(false);
        /**
         * Boolean state to update the MUI TextField label's position since it does not always
         * float up when the field has a value.
         */
        const [shrink, setShrink] = useState<boolean>(false);
        /**
         * Boolean state to disable the initial animation of the label on screen render so that the label
         * appears to have been already set correctly if it wasn't already.
         */
        const [disableAnimation, setDisableAnimation] = useState<boolean>(true);
        /**
         * The on-screen keyboard input ref is used to prevent a stale state when we call to update it
         * from keyboard context functions. It is used to prevent the onChange function that recieves a
         * callback from the pattern mask from resetting the caret position incorrectly after we set it
         * using the keyboard context setValue function.
         */
        const wasOSKInput = useRef<boolean>(true);
        useScrollToOnError({
            scrollable: inputRef,
            name: name,
        });

        const locale = useLocale();
        const {
            dealer: { primaryLanguage },
        } = useKioskState();

        useEffect(() => {
            if (tabFocusRefContext) {
                tabFocusRefContext.current = inputRef.current;
            }
        }, [tabFocusRefContext]);

        useEffect(() => {
            /**
             * Updates the internal ref value when formik's value changes externally.
             */
            if (field.value !== valueRef.current && (field.value !== null || field.value !== undefined) && valueRef.current.indexOf('*') === -1) {
                valueRef.current = field.value;
            }

            /**
             * Sets shrink to true if there is an initial field value from Formik
             * so that the label position is correctly set.
             */
            if (field.value && field.value.length > 0 && !shrink) {
                setShrink(true);
            }

            /**
             * Sets a 1 second timeout to disable the animation before re-enabling it.
             * This is intended only on screen render so the label position does not float up
             * as the screen renders.
             */
            setTimeout(() => {
                setDisableAnimation(false);
            }, 1);
        }, [field.value, shrink]);

        useEffect(() => {
            /**
             * Updates the internal caret position when the internal MUI value finishes changing from the
             * pattern mask.
             */
            if (inputRef.current) {
                inputRef.current.setSelectionRange(caretPositionRef.current, caretPositionRef.current);
            }

            if (wasOSKInput.current) {
                wasOSKInput.current = false;
            }
        }, [inputRef.current?.value]);

        /**
         * Used by the keyboard context and is described on the declaration there.
         */
        const getValue = (): GetValueReturn => {
            /**
             * We initialize the wasOSKInput ref here to be true so that onChange doesn't
             * set a new caret position when it runs.
             */
            wasOSKInput.current = true;

            if (inputRef.current && inputRef.current.selectionStart !== null && inputRef.current.selectionEnd !== null) {
                const selectionStart: number = inputRef.current.selectionStart;
                const selectionEnd: number = inputRef.current.selectionEnd;

                return {
                    inputValue: valueRef.current,
                    caretPositionStart: selectionStart,
                    caretPositionEnd: selectionEnd,
                };
            }
            else {
                return {
                    inputValue: valueRef.current,
                    caretPositionStart: valueRef.current.length,
                    caretPositionEnd: valueRef.current.length,
                };
            }
        };

        /**
         * Used by the keyboard context and is described on the declaration there.
         */
        const setValue = ({ value, caretPosition }: SetValueProps): void => {
            caretPositionRef.current = caretPosition;
            onChangeCallback && onChangeCallback();
            valueRef.current = value;
            setFieldValue(name, value);
        };

        /**
         * Used by the keyboard context and is described on the declaration there.
         */
        const onInputChange = ({ value, caretPosition, wasBackspace, wasSelectionRange }: OnInputChangeProps): OnInputChangeReturn => {
            return {
                value: value,
                caretPosition: caretPosition,
            };
        };

        /**
         * Used by the keyboard context and is described on the declaration there.
         */
        const onKeyboardInput = (): void => {
            if (inputRef.current && !isFocused.current) {
                inputRef.current.focus();
            }
        };

        const { onFocus, onBlur } = useKeyboardIntegration({
            fieldValue: field.value,
            getValue: getValue,
            setValue: setValue,
            onInputChange: onInputChange,
            onKeyboardInput: onKeyboardInput,
            keyboardLayout: keyboardLayout,
            shiftAfterPeriod: shiftAfterPeriod,
            shiftAfterSpace: shiftAfterSpace,
            shiftAfterAmpersand: shiftAfterAmpersand,
            hideKeyboardOnClickOut: hideKeyboardOnClickOut,
            capsLock: capsLock,
        });

        const handleOnBlur = () => {
            onBlur();
            if (valueRef.current.length === 0 || valueRef.current === null) {
                // Handles letting the label position float down if no value is present when we blur.
                setShrink(false);
            }
            isFocused.current = false;
        };

        /**
         * This function is called by the pattern mask onAccept function for on-screen keyboard input
         * or called when phsyical keyboard input is entered.
         */
        const handleOnChange = (event: ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
            if (!wasOSKInput.current) {
                caretPositionRef.current = event.target.selectionEnd;
                onChangeCallback && onChangeCallback();
                valueRef.current = event.target.value;
                setFieldValue(name, event.target.value);
            }
        };

        const handleOnFocus = (event: FocusEvent<HTMLInputElement | HTMLTextAreaElement>) => {
            if (!shrink) {
                // Handles setting the label to float up when the field is focused.
                setShrink(true);
            }
            if (event.target.value.indexOf('*') !== -1) {
                // Handles unmasking if the value was masked.
                valueRef.current = '';
                setFieldValue(name, '');
            }
            if (!wasUnmasked) {
                // Handles preventing the field from remasking after the field has been focused once.
                setWasUnmasked(true);
            }
            isFocused.current = true;
            onFocus(event);
        };

        return (
            <BaseTextField
                name={name}
                id={id}
                title={title}
                label={label}
                ariaLabel={ariaLabel}
                inputMode={'text'}
                type={'text'}
                inputRef={inputRef}
                value={field.value ?? ''}
                error={meta.error}
                touched={meta.touched || submitCount > 0}
                disabled={disabled}
                required={required}
                autoFocus={autoFocus}
                fullWidth={fullWidth}
                seventyFivePercentWidth={seventyFivePercentWidth}
                capsLock={capsLock}
                onFocus={handleOnFocus}
                onChange={handleOnChange}
                onBlur={handleOnBlur}
                InputLabelProps={{
                    shrink: shrink,
                    style: {
                        transition: disableAnimation ? 'none' : undefined,
                    },
                }}
                InputProps={{
                    inputComponent: EmailPatternMask as any,
                    inputProps: {
                        isMasked: initiallyMasked && !isFocused.current && !wasUnmasked,
                    },
                }}
                onKeyDown={() => {
                    /**
                     * This ensures that if for some reason the on-screen keyboard boolean was true, it is set to false
                     * when keyboard input occurs.
                     */
                    if (wasOSKInput.current) {
                        wasOSKInput.current = false;
                    }
                }}
                safariHack={locale === primaryLanguage ? 'primarylang' : 'alternatelang'}
            />
        );
    },
);
